import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useNavigate } from "react-router-dom";
import { Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { TrendingDown, TrendingUp, DollarSign, Calendar, Loader2, CalendarIcon, CreditCard, Repeat } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { dashboardApi, DashboardResponseDto, accountSettingsApi, type CommitmentItem } from "@/lib/api";
import { TrendingDown as TrendDown, TrendingUp as TrendUp } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay, parse } from "date-fns";
import { he } from "date-fns/locale";
import { useState, useEffect, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import dashboardHero from "@/assets/dashboard-hero.jpg";

const COMMITMENTS_PREVIEW = 5;

type DateRangePreset = 'current-month' | 'last-month' | 'prev-month' | 'last-3-months' | 'custom';

interface DateRange {
  from: Date;
  to: Date;
}

function CommitmentRow({
  item,
  formatAmount,
  t,
  rowVariant,
}: {
  item: CommitmentItem;
  formatAmount: (amount: number) => string;
  t: (key: string, opts?: any) => string;
  rowVariant: 'standing' | 'installment';
}) {
  const sourceBadgeText =
    item.source === 'cc-sync'
      ? t('dashboard.fromCcSync')
      : rowVariant === 'installment'
        ? t('transactions.installment')
        : t('dashboard.fromRecurring');
  const sourceBadgeClass =
    item.source === 'cc-sync'
      ? 'bg-blue-500/12 text-blue-200 border border-blue-500/25'
      : rowVariant === 'installment'
        ? 'bg-orange-500/12 text-orange-200 border border-orange-500/25'
        : 'bg-muted/50 text-muted-foreground border border-border/80';

  return (
    <div className="flex items-center justify-between p-2.5 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Category color dot */}
        <div
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: item.categoryColor || '#94a3b8' }}
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium truncate">{item.description}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {item.categoryName && <span>{item.categoryName}</span>}
            {item.dayOfMonth && (
              <span>· {t('dashboard.dayOfMonthShort', { day: item.dayOfMonth })}</span>
            )}
            {item.installmentInfo && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 text-orange-600 border-orange-200">
                {item.installmentInfo}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-semibold" dir="ltr">
          {formatAmount(item.amount)}
        </span>
        <Badge variant="secondary" className={cn('text-[10px] px-1.5 py-0 h-4', sourceBadgeClass)}>
          {sourceBadgeText}
        </Badge>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { formatAmount } = useCurrency();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Date range state
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('current-month');
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>(); // Temporary range while selecting
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get date range based on selected preset
  const getDateRange = (): DateRange => {
    const now = new Date();
    
    switch (selectedPreset) {
      case 'current-month':
        return {
          from: startOfMonth(now),
          to: endOfMonth(now)
        };
      case 'last-month':
        const lastMonth = subMonths(now, 1);
        return {
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth)
        };
      case 'prev-month':
        const prevMonth = subMonths(now, 1);
        return {
          from: startOfMonth(prevMonth),
          to: endOfMonth(prevMonth)
        };
      case 'last-3-months':
        const threeMonthsAgo = subMonths(now, 3);
        return {
          from: startOfMonth(threeMonthsAgo),
          to: endOfMonth(now)
        };
      case 'custom':
        return customDateRange || {
          from: startOfMonth(now),
          to: endOfMonth(now)
        };
      default:
        return {
          from: startOfMonth(now),
          to: endOfMonth(now)
        };
    }
  };

  const dateRange = getDateRange();
  const startDate = format(dateRange.from, 'yyyy-MM-dd');
  const endDate = format(dateRange.to, 'yyyy-MM-dd');

  // Fetch dashboard data using the new API
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['dashboard', startDate, endDate, selectedPreset],
    queryFn: () => dashboardApi.getDashboardData({ startDate, endDate }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch account settings (contains average statistics) - only fetch once
  const { data: accountSettings } = useQuery({
    queryKey: ['accountSettings'],
    queryFn: () => accountSettingsApi.getSettings(),
    staleTime: 10 * 60 * 1000,
  });

  const { data: commitments } = useQuery({
    queryKey: ['commitments'],
    queryFn: () => dashboardApi.getCommitments(),
    staleTime: 5 * 60 * 1000,
  });

  const currentCalendarMonth = format(new Date(), 'yyyy-MM');

  /** Rolling last 12 months (inclusive); BE returns monthly buckets for this window. */
  const cashflowRangeStrings = useMemo(() => {
    const now = new Date();
    const from = startOfMonth(subMonths(now, 11));
    const to = endOfMonth(now);
    return { from: format(from, 'yyyy-MM-dd'), to: format(to, 'yyyy-MM-dd') };
  }, [currentCalendarMonth]);

  const { data: cashflowData, isLoading: isCashflowLoading } = useQuery({
    queryKey: ['dashboard-cashflow', cashflowRangeStrings.from, cashflowRangeStrings.to],
    queryFn: () => dashboardApi.getDashboardData({ startDate: cashflowRangeStrings.from, endDate: cashflowRangeStrings.to }),
    staleTime: 5 * 60 * 1000,
  });

  const cashflowMonthlyRows = useMemo(() => {
    if (!cashflowData?.monthlyBalance?.length) return [];
    const isHebrew = i18n.language === 'he';
    const locale = isHebrew ? he : undefined;
    const months = cashflowData.monthlyBalance;
    const y0 = months[0].month.slice(0, 4);
    const y1 = months[months.length - 1].month.slice(0, 4);
    const showYearOnAxis = y0 !== y1;
    return months.map((monthData) => {
      const date = new Date(monthData.month + '-01');
      return {
        monthKey: monthData.month,
        monthLine: format(date, 'MMM', { locale }),
        yearLine: showYearOnAxis ? format(date, 'yyyy') : '',
        expenses: monthData.totalExpenses,
        income: monthData.totalIncome,
        balance: monthData.balance,
      };
    });
  }, [cashflowData, i18n.language]);

  const cashflowAxisTwoLines =
    cashflowMonthlyRows.length > 0 && Boolean(cashflowMonthlyRows[0].yearLine);

  /** Subtitle: prefer span from returned months; else fall back to requested range. */
  const cashflowRangeLabel = useMemo(() => {
    const locale = i18n.language === 'he' ? he : undefined;
    if (cashflowMonthlyRows.length > 0) {
      const firstKey = cashflowMonthlyRows[0].monthKey;
      const lastKey = cashflowMonthlyRows[cashflowMonthlyRows.length - 1].monthKey;
      const d0 = parse(firstKey + '-01', 'yyyy-MM-dd', new Date());
      const d1 = parse(lastKey + '-01', 'yyyy-MM-dd', new Date());
      const y0 = format(d0, 'yyyy');
      const y1 = format(d1, 'yyyy');
      if (y0 === y1) return format(d1, 'MMMM yyyy', { locale });
      return `${format(d0, 'MMM yyyy', { locale })} – ${format(d1, 'MMM yyyy', { locale })}`;
    }
    const fromD = parse(cashflowRangeStrings.from, 'yyyy-MM-dd', new Date());
    const toD = parse(cashflowRangeStrings.to, 'yyyy-MM-dd', new Date());
    const yFrom = format(fromD, 'yyyy');
    const yTo = format(toD, 'yyyy');
    if (yFrom === yTo) return format(toD, 'MMMM yyyy', { locale });
    return `${format(fromD, 'MMM yyyy', { locale })} – ${format(toD, 'MMM yyyy', { locale })}`;
  }, [cashflowMonthlyRows, cashflowRangeStrings.from, cashflowRangeStrings.to, i18n.language]);

  // Handle click on month bar to filter dashboard by that month
  // NOTE: This hook must be called before any conditional returns
  const handleMonthBarClick = useCallback((data: any) => {
    if (data && data.monthKey) {
      const [year, month] = data.monthKey.split('-');
      const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      setCustomDateRange({
        from: startOfMonth(monthDate),
        to: endOfMonth(monthDate)
      });
      setSelectedPreset('custom');
    }
  }, []);

  // Show skeleton loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Hero Section Skeleton */}
        <Skeleton className="h-32 w-full rounded-xl" />

        {/* Date Range Filter Skeleton */}
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-8 w-24" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-28" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
        </Card>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-8 w-16" />
                </div>
                <Skeleton className="h-8 w-8 rounded" />
              </div>
            </Card>
          ))}
        </div>

        {/* Cashflow + recent row skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <Card className="p-4 md:p-6 lg:col-span-2">
            <div className="mb-4 space-y-2">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-3 w-40" />
            </div>
            <Skeleton className="h-64 w-full rounded-lg" />
          </Card>
          <Card className="p-4 md:p-6 lg:col-span-1">
            <div className="flex justify-between mb-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-2.5 h-2.5 rounded-full" />
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                  <Skeleton className="h-4 w-14" />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Expenses by Category Skeleton */}
        <Card className="p-6">
          <Skeleton className="h-6 w-40 mx-auto mb-4" />
          <div className="h-64 flex items-center justify-center">
            <Skeleton className="h-32 w-full max-w-md rounded-lg" />
          </div>
        </Card>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-destructive mb-2">{t('common.error')}</p>
          <p className="text-muted-foreground text-sm">
            {error instanceof Error ? error.message : 'Failed to load dashboard data'}
          </p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  // Enhanced color palette for better visibility
  const enhancedColors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
    '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
  ];

  // Process expenses by category for the pie chart
  const expensesByCategory = dashboardData.expensesByCategory
    .map((categoryData, index) => {
      // Handle both camelCase (new API) and lowercase (legacy API) field names
      const apiData = categoryData as Record<string, unknown>;
      const name = (apiData.categoryName || apiData.categoryname || `Category ${index + 1}`) as string;
      const value = parseFloat(String(apiData.total)) || 0;
      const transactionCount = (apiData.transactionCount || apiData.totalexpenses || 0) as number;
      
      // Use enhanced colors for better visibility, fallback to API color, then to enhanced palette
      let color = (apiData.categoryColor || apiData.categorycolor) as string | undefined;
      if (!color || color === '#000000' || color === '#ffffff') {
        color = enhancedColors[index % enhancedColors.length];
      }
      
      return {
        name,
        value,
        color,
        transactionCount: typeof transactionCount === 'string' ? parseInt(transactionCount, 10) : transactionCount,
      };
    })
    .filter(item => item.value > 0);

  const getRecentTransactionDotColor = (categoryName: string | undefined, amount: number): string => {
    const name = (categoryName ?? '').trim();
    if (!name) {
      return amount < 0 ? 'hsl(var(--expense))' : 'hsl(var(--success))';
    }
    const row = expensesByCategory.find((c) => c.name?.trim() === name);
    if (row?.color && row.color !== '#000000' && row.color !== '#ffffff') {
      return row.color;
    }
    return amount < 0 ? 'hsl(var(--expense))' : 'hsl(var(--success))';
  };

  const hasStandingOrders = !!commitments?.standingOrders?.length;
  const hasInstallments = !!commitments?.installments?.length;
  const hasAnyCommitments = hasStandingOrders || hasInstallments;
  const bottomInsightsGridClass = hasAnyCommitments ? 'lg:grid-cols-2' : 'grid-cols-1';
  const commitmentPanelCount = hasAnyCommitments ? 2 : 1;
  const categoryChartYAxisWidth = hasAnyCommitments ? 96 : 120;
  const categoryChartBarSize = hasAnyCommitments ? 26 : 32;
  const commitmentsViewAll =
    !!commitments &&
    (commitments.standingOrders.length > COMMITMENTS_PREVIEW ||
      commitments.installments.length > COMMITMENTS_PREVIEW);

  return (
    <div className="page-shell">
      {/* Hero Section */}
      <div className={`relative overflow-hidden rounded-2xl p-4 md:p-6 border ${
        dashboardData.balance >= 0 
          ? 'bg-gradient-to-r from-green-500/80 to-green-600/70 text-white border-green-500/35' 
          : 'bg-gradient-to-r from-red-500/80 to-red-600/70 text-white border-red-500/35'
      }`}>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${dashboardHero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 text-center">
          <h1 className="text-2xl md:text-3xl font-bold mb-2 text-white">{t('dashboard.title')}</h1>
          <p className="text-white/80 text-sm md:text-lg">
            {t('dashboard.subtitle')}
          </p>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="p-3 md:p-4 border-border/90">
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 md:items-center md:justify-between">
          {/* Filter Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-nowrap gap-2 md:gap-3">
            <Button
              variant={selectedPreset === 'current-month' ? 'default' : 'outline'}
              size="sm"
              className={cn(
                "text-xs md:text-sm whitespace-nowrap px-2 md:px-3 min-h-[36px]",
                selectedPreset === 'current-month' && "ring-2 ring-primary/20"
              )}
              onClick={() => {
                setSelectedPreset('current-month');
                setCustomDateRange(undefined);
              }}
            >
              {t('dashboard.filters.currentMonth', 'Current Month')}
            </Button>
            <Button
              variant={selectedPreset === 'last-month' ? 'default' : 'outline'}
              size="sm"
              className={cn(
                "text-xs md:text-sm whitespace-nowrap px-2 md:px-3 min-h-[36px]",
                selectedPreset === 'last-month' && "ring-2 ring-primary/20"
              )}
              onClick={() => {
                setSelectedPreset('last-month');
                setCustomDateRange(undefined);
              }}
            >
              {t('dashboard.filters.lastMonth', 'Last Month')}
            </Button>
            <Button
              variant={selectedPreset === 'last-3-months' ? 'default' : 'outline'}
              size="sm"
              className={cn(
                "text-xs md:text-sm whitespace-nowrap px-2 md:px-3 col-span-2 sm:col-span-1 min-h-[36px]",
                selectedPreset === 'last-3-months' && "ring-2 ring-primary/20"
              )}
              onClick={() => {
                setSelectedPreset('last-3-months');
                setCustomDateRange(undefined);
              }}
            >
              {t('dashboard.filters.last3Months', 'Last 3 Months')}
            </Button>
          </div>
          
          {/* Custom Date Range and Date Display */}
          <div className="flex flex-col sm:flex-row md:flex-row items-start sm:items-center gap-2 md:gap-3">
            <Popover 
              open={isCalendarOpen} 
              onOpenChange={(open) => {
                setIsCalendarOpen(open);
                // Initialize temp range with current custom range when opening
                if (open) {
                  setTempDateRange(customDateRange);
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant={selectedPreset === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    "justify-start text-left font-normal text-xs md:text-sm w-full sm:w-auto md:w-64 min-h-[36px]",
                    !customDateRange && "text-muted-foreground",
                    selectedPreset === 'custom' && "ring-2 ring-primary/20"
                  )}
                >
                  <CalendarIcon className="mr-1 md:mr-2 h-3 md:h-4 w-3 md:w-4" />
                  {selectedPreset === 'custom' && customDateRange ? (
                    <span className="truncate">
                      {format(customDateRange.from, "MMM dd")} - {format(customDateRange.to, "MMM dd, yyyy")}
                    </span>
                  ) : (
                    t('dashboard.filters.customRange', 'Custom Range')
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex flex-col">
                  <CalendarComponent
                    initialFocus
                    mode="range"
                    defaultMonth={tempDateRange?.from || customDateRange?.from}
                    selected={tempDateRange}
                    onSelect={(range) => {
                      if (range?.from) {
                        // Update temporary date range while selecting
                        setTempDateRange({
                          from: startOfDay(range.from),
                          to: range.to ? endOfDay(range.to) : endOfDay(range.from)
                        });
                      }
                    }}
                    numberOfMonths={isMobile ? 1 : 2}
                  />
                  <div className="border-t p-3 flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setTempDateRange(undefined);
                        setIsCalendarOpen(false);
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (tempDateRange) {
                          setCustomDateRange(tempDateRange);
                          setSelectedPreset('custom');
                        }
                        setIsCalendarOpen(false);
                      }}
                      disabled={!tempDateRange}
                    >
                      {t('common.apply', 'Apply')}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            <div className="text-xs md:text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded flex items-center gap-2 whitespace-nowrap">
              {isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
              {format(dateRange.from, "MMM dd")} - {format(dateRange.to, "MMM dd, yyyy")}
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Cards */}
      <div className={cn(
        "grid grid-cols-1 gap-3 md:gap-6",
        commitments && (commitments.standingOrders.length > 0 || commitments.installments.length > 0)
          ? "md:grid-cols-4"
          : "md:grid-cols-3"
      )}>
        {/* Balance Card */}
        <Card className={`p-4 md:p-6 border ${dashboardData.balance >= 0 ? 'success-gradient border-green-500/30' : 'expense-gradient border-red-500/30'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs md:text-sm ${dashboardData.balance >= 0 ? 'text-success-foreground/80' : 'text-expense-foreground/80'}`}>
                {t('dashboard.netAmount')}
              </p>
              <p className={`text-xl md:text-2xl font-bold ${dashboardData.balance >= 0 ? 'text-success-foreground' : 'text-expense-foreground'}`}>
                {formatAmount(dashboardData.balance)}
              </p>
            </div>
            <DollarSign className={`h-6 md:h-8 w-6 md:w-8 ${dashboardData.balance >= 0 ? 'text-success-foreground' : 'text-expense-foreground'}`} />
          </div>
        </Card>

        {/* Total Expenses with Recurring & Average */}
        <Card className="p-4 md:p-6 card-gradient">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-xs md:text-sm text-muted-foreground">{t('dashboard.totalExpenses')}</p>
              <p className="text-xl md:text-2xl font-bold text-expense">{formatAmount(dashboardData.totalExpenses)}</p>
              
              {/* Recurring breakdown - use absolute value since expenses are negative */}
              {(() => {
                const recurringAmount = Math.abs(Number(dashboardData.totalRecurringPayments) || 0);
                if (recurringAmount > 0) {
                  return (
                    <p className="text-[10px] md:text-xs text-primary/80 flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-primary" />
                      {t('dashboard.includingRecurring', 'incl. {{amount}} recurring', { amount: formatAmount(recurringAmount) })}
                    </p>
                  );
                }
                return null;
              })()}
              
              {/* Average comparison */}
              {accountSettings?.averageMonthlyExpenses && (
                <div className="flex items-center gap-2 mt-1">
                  {(() => {
                    const diff = dashboardData.totalExpenses - accountSettings.averageMonthlyExpenses;
                    const percent = Math.round((diff / accountSettings.averageMonthlyExpenses) * 100);
                    const isOver = diff > 0;
                    return (
                      <span className={`text-[10px] md:text-xs font-medium flex items-center gap-1 ${isOver ? 'text-red-500' : 'text-green-600'}`}>
                        {isOver ? <TrendUp className="h-3 w-3" /> : <TrendDown className="h-3 w-3" />}
                        {isOver ? '+' : ''}{percent}% {t('dashboard.vsAvg', 'vs avg')}
                      </span>
                    );
                  })()}
                  <span className="text-[10px] text-muted-foreground">
                    ({formatAmount(accountSettings.averageMonthlyExpenses)})
                  </span>
                </div>
              )}
            </div>
            <TrendingDown className="h-6 md:h-8 w-6 md:w-8 text-expense" />
          </div>
        </Card>

        {/* Total Income with Average */}
        <Card className="p-4 md:p-6 card-gradient">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-xs md:text-sm text-muted-foreground">{t('dashboard.totalIncome')}</p>
              <p className="text-xl md:text-2xl font-bold text-success">{formatAmount(dashboardData.totalIncome)}</p>
              
              {/* Average comparison */}
              {accountSettings?.averageMonthlyIncome && (
                <div className="flex items-center gap-2 mt-1">
                  {(() => {
                    const diff = dashboardData.totalIncome - accountSettings.averageMonthlyIncome;
                    const percent = Math.round((diff / accountSettings.averageMonthlyIncome) * 100);
                    const isPositive = diff >= 0;
                    return (
                      <span className={`text-[10px] md:text-xs font-medium flex items-center gap-1 ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                        {isPositive ? <TrendUp className="h-3 w-3" /> : <TrendDown className="h-3 w-3" />}
                        {isPositive ? '+' : ''}{percent}% {t('dashboard.vsAvg', 'vs avg')}
                      </span>
                    );
                  })()}
                  <span className="text-[10px] text-muted-foreground">
                    ({formatAmount(accountSettings.averageMonthlyIncome)})
                  </span>
                </div>
              )}
            </div>
            <TrendingUp className="h-6 md:h-8 w-6 md:w-8 text-success" />
          </div>
        </Card>

        {/* Commitments Card — standing orders + installments in one box */}
        {commitments && (commitments.standingOrders.length > 0 || commitments.installments.length > 0) && (
          <Card className="p-4 md:p-6 card-gradient">
            <p className="text-xs md:text-sm text-muted-foreground mb-2">{t('dashboard.commitments')}</p>
            <p className="text-xl md:text-2xl font-bold" dir="ltr">
              {formatAmount(commitments.standingOrdersTotal + commitments.installmentsTotal)}
            </p>
            <div className="mt-2 space-y-1.5">
              {commitments.standingOrders.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] md:text-xs text-muted-foreground flex items-center gap-1">
                    <Repeat className="h-3 w-3 text-purple-500" />
                    {t('dashboard.standingOrders')}
                  </span>
                  <span className="text-[10px] md:text-xs font-medium text-purple-600" dir="ltr">
                    {formatAmount(commitments.standingOrdersTotal)} ({commitments.standingOrders.length})
                  </span>
                </div>
              )}
              {commitments.installments.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] md:text-xs text-muted-foreground flex items-center gap-1">
                    <CreditCard className="h-3 w-3 text-orange-500" />
                    {t('dashboard.installments')}
                  </span>
                  <span className="text-[10px] md:text-xs font-medium text-orange-600" dir="ltr">
                    {formatAmount(commitments.installmentsTotal)} ({commitments.installments.length})
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Cashflow chart + recent transactions — same row on large screens */}
      <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3 lg:items-stretch">
        <Card className="card-gradient flex min-h-0 flex-col p-4 md:p-6 lg:col-span-2">
          <div className="shrink-0">
            <h3 className="text-base font-semibold md:text-lg">{t('dashboard.cashflow')}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">{cashflowRangeLabel}</p>
          </div>
          <p className="mb-1 mt-2 shrink-0 text-xs text-muted-foreground">
            {t('dashboard.clickToFilter', 'Click on a month to filter')}
          </p>
          <div className="mt-1 h-64 min-h-[220px] flex-1 md:h-72">
            {isCashflowLoading ? (
              <Skeleton className="h-full min-h-[220px] w-full rounded-lg" />
            ) : cashflowMonthlyRows.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={cashflowMonthlyRows}
                  margin={{ top: 8, right: 8, left: 4, bottom: cashflowAxisTwoLines ? 28 : 12 }}
                  onClick={(data) => {
                    if (data?.activePayload?.length) {
                      handleMonthBarClick(data.activePayload[0].payload);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/50" />
                  <XAxis
                    dataKey="monthKey"
                    type="category"
                    interval={0}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tick={(props: { x: number; y: number; index: number }) => {
                      const row = cashflowMonthlyRows[props.index];
                      if (!row) return null;
                      const hasYear = Boolean(row.yearLine);
                      return (
                        <g transform={`translate(${props.x},${props.y})`}>
                          <text
                            textAnchor="middle"
                            fill="hsl(var(--muted-foreground))"
                            fontSize={11}
                            dy={hasYear ? 0 : 4}
                          >
                            {row.monthLine}
                          </text>
                          {hasYear ? (
                            <text
                              textAnchor="middle"
                              fill="hsl(var(--muted-foreground))"
                              fontSize={10}
                              dy={14}
                              opacity={0.88}
                            >
                              {row.yearLine}
                            </text>
                          ) : null}
                        </g>
                      );
                    }}
                    height={cashflowAxisTwoLines ? 40 : 28}
                  />
                  <YAxis
                    tickFormatter={(value) => {
                      if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                      return value.toString();
                    }}
                    width={48}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip
                    formatter={(value, name) => [formatAmount(value as number), name]}
                    labelFormatter={(_, items) => {
                      const key = items?.[0]?.payload?.monthKey as string | undefined;
                      if (!key) return '';
                      const d = parse(key + '-01', 'yyyy-MM-dd', new Date());
                      return format(d, 'MMM yyyy', {
                        locale: i18n.language === 'he' ? he : undefined,
                      });
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                    }}
                    cursor={{ fill: 'hsl(var(--muted))', opacity: 0.25 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="income"
                    fill="hsl(var(--success))"
                    name={t('dashboard.income')}
                    radius={[5, 5, 0, 0]}
                    maxBarSize={18}
                    cursor="pointer"
                    className="transition-opacity hover:opacity-90"
                  />
                  <Bar
                    dataKey="expenses"
                    fill="hsl(var(--expense))"
                    name={t('dashboard.expenses')}
                    radius={[5, 5, 0, 0]}
                    maxBarSize={18}
                    cursor="pointer"
                    className="transition-opacity hover:opacity-90"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full min-h-[200px] items-center justify-center rounded-lg border border-dashed border-border/60 bg-muted/20">
                <p className="px-4 text-center text-sm text-muted-foreground">
                  {t('dashboard.noExpenseData', 'No expense data available')}
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card className="card-gradient flex min-h-0 flex-col p-4 md:p-6 lg:col-span-1">
          <div className="mb-3 flex shrink-0 items-start justify-between gap-2">
            <h3 className="text-base font-semibold">{t('dashboard.recentTransactions')}</h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-auto shrink-0 px-2 py-1 text-xs text-primary"
              onClick={() => navigate('/transactions')}
            >
              {t('dashboard.viewAllTransactions')}
            </Button>
          </div>
          <div className="min-h-0 max-h-[min(420px,55vh)] flex-1 space-y-2 overflow-y-auto lg:max-h-[min(520px,70vh)]">
            {dashboardData.latestTransactions.map((transaction) => {
              const apiColor = transaction.categoryColor;
              const dotColor =
                apiColor && apiColor !== '#000000' && apiColor !== '#ffffff'
                  ? apiColor
                  : getRecentTransactionDotColor(transaction.categoryName, transaction.amount);
              return (
              <div
                key={transaction.id}
                className="flex items-center justify-between gap-2 rounded-lg bg-muted/50 p-2.5"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 shrink-0 rounded-full border border-border/40 shadow-sm"
                    style={{ backgroundColor: dotColor }}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{transaction.description}</p>
                    <p className="truncate text-xs text-muted-foreground">{transaction.categoryName}</p>
                  </div>
                </div>
                <div className="shrink-0 text-end">
                  <p
                    className={cn(
                      'text-sm font-semibold',
                      transaction.amount < 0 ? 'text-expense' : 'text-success',
                    )}
                    dir="ltr"
                  >
                    {formatAmount(Math.abs(transaction.amount))}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {format(new Date(transaction.date), 'MMM d', {
                      locale: i18n.language === 'he' ? he : undefined,
                    })}
                  </p>
                </div>
              </div>
            );
            })}
          </div>
        </Card>
      </div>

      {/* Expenses by category + commitments — one row when both commitment panels exist */}
      <div className={cn('grid gap-4 md:gap-6 lg:items-stretch', bottomInsightsGridClass)}>
      <Card className="card-gradient flex min-h-0 flex-col p-4 md:p-6">
        <h3 className="mb-2 text-center text-base font-semibold md:mb-3 md:text-lg">{t('dashboard.expensesByCategory')}</h3>
        <p className="mb-2 text-center text-xs text-muted-foreground">
          {t('dashboard.clickToViewTransactions')}
        </p>
        <div
          className="min-h-[260px] flex-1 overflow-y-auto md:min-h-[300px]"
          style={{ height: Math.max(260, Math.min(560, expensesByCategory.length * 44)) }}
        >
          {expensesByCategory.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={expensesByCategory}
                layout="vertical"
                margin={{ top: 5, right: 44, left: 8, bottom: 5 }}
                onClick={(data) => {
                  if (data && data.activePayload && data.activePayload.length > 0) {
                    const categoryName = data.activePayload[0].payload.name;
                    const dateRange = getDateRange();
                    const fromDate = format(dateRange.from, 'yyyy-MM-dd');
                    const toDate = format(dateRange.to, 'yyyy-MM-dd');
                    navigate(`/transactions?category=${encodeURIComponent(categoryName)}&from=${fromDate}&to=${toDate}`);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis 
                  type="number"
                  tickFormatter={(value) => {
                    if (value >= 1000) {
                      return `${(value / 1000).toFixed(0)}K`;
                    }
                    return value.toString();
                  }}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  type="category"
                  dataKey="name"
                  width={categoryChartYAxisWidth}
                  tick={{ fontSize: commitmentPanelCount === 2 ? 11 : 13, fontWeight: 500 }}
                  tickLine={false}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || !payload.length) return null;
                    
                    const data = payload[0].payload;
                    const total = expensesByCategory.reduce((sum, item) => sum + item.value, 0);
                    const percentageNum = total > 0 ? (data.value / total) * 100 : 0;
                    // Show 2 decimal places if < 1%, otherwise show 1 decimal place
                    const percentage = percentageNum < 1 && percentageNum > 0 
                      ? percentageNum.toFixed(2) 
                      : percentageNum.toFixed(1);
                    
                    return (
                      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                        <p className="font-semibold text-gray-900 dark:text-white mb-1">{data.name}</p>
                        <p className="text-gray-600 dark:text-gray-300">
                          {formatAmount(data.value)} ({percentage}%)
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {data.transactionCount} {t('dashboard.transactions')}
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar 
                  dataKey="value" 
                  radius={[0, 6, 6, 0]}
                  barSize={categoryChartBarSize}
                  label={({ x, y, width, height, value }) => {
                    const total = expensesByCategory.reduce((sum, item) => sum + item.value, 0);
                    const percentageNum = total > 0 ? (value / total) * 100 : 0;
                    // Show 2 decimal places if < 1%, otherwise show as integer
                    const percentage = percentageNum < 1 && percentageNum > 0 
                      ? percentageNum.toFixed(2) 
                      : percentageNum.toFixed(0);
                    return (
                      <text
                        x={x + width + 8}
                        y={y + height / 2}
                        fill="hsl(var(--muted-foreground))"
                        fontSize={12}
                        fontWeight={500}
                        dominantBaseline="middle"
                      >
                        {percentage}%
                      </text>
                    );
                  }}
                >
                  {expensesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground text-center">
                {t('dashboard.noExpenseData', 'No expense data available')}
              </p>
            </div>
          )}
        </div>
      </Card>

          {hasAnyCommitments && commitments && (
            <Card className="card-gradient flex min-h-0 flex-col p-4 md:p-6">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold md:text-lg">
                  <span className="flex items-center gap-1.5">
                    <Repeat className="h-5 w-5 shrink-0 text-purple-500" />
                    <CreditCard className="h-5 w-5 shrink-0 text-orange-500" />
                  </span>
                  {t('dashboard.commitments')}
                </h3>
                <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
                  {commitmentsViewAll && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto px-2 py-1 text-xs text-primary"
                      onClick={() => navigate('/periodic-payments')}
                    >
                      {t('dashboard.viewAllPeriodic')}
                    </Button>
                  )}
                  {hasStandingOrders && (
                    <Badge variant="outline" className="text-purple-600 border-purple-200 bg-purple-50">
                      {t('dashboard.standingOrders')}: {formatAmount(commitments.standingOrdersTotal)}
                      {t('dashboard.perMonth')}
                    </Badge>
                  )}
                  {hasInstallments && (
                    <Badge variant="outline" className="text-orange-600 border-orange-200 bg-orange-50">
                      {t('dashboard.installments')}: {formatAmount(commitments.installmentsTotal)}
                      {t('dashboard.perMonth')}
                    </Badge>
                  )}
                </div>
              </div>

              {hasStandingOrders && (
                <div className="min-h-0">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    {t('dashboard.standingOrders')}
                  </p>
                  <div className="space-y-2">
                    {commitments.standingOrders.slice(0, COMMITMENTS_PREVIEW).map((item) => (
                      <CommitmentRow
                        key={`standing-${item.id}`}
                        item={item}
                        formatAmount={formatAmount}
                        t={t}
                        rowVariant="standing"
                      />
                    ))}
                  </div>
                </div>
              )}

              {hasStandingOrders && hasInstallments && (
                <div className="my-4 border-t border-border/60" aria-hidden />
              )}

              {hasInstallments && (
                <div className="min-h-0">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    {t('dashboard.installments')}
                  </p>
                  <div className="space-y-2">
                    {commitments.installments.slice(0, COMMITMENTS_PREVIEW).map((item) => (
                      <CommitmentRow
                        key={`installment-${item.id}`}
                        item={item}
                        formatAmount={formatAmount}
                        t={t}
                        rowVariant="installment"
                      />
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
      </div>
    </div>
  );
}